const EMPTY_ICON = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18M9 21V9"/></svg>`;

export interface TableColumn<T = Record<string, unknown>> {
  key: string;
  header: string;
  width?: string;
  align?: "left" | "center" | "right";
  render?: (row: T) => string;
}

export class AppDataTable<T extends Record<string, unknown> = Record<string, unknown>> extends HTMLElement {
  private _columns: TableColumn<T>[] = [];
  private _data: T[] = [];
  private _loading: boolean = false;
  private _emptyMessage: string = "Nenhum registro encontrado.";
  private _emptyAction?: string;

  set columns(cols: TableColumn<T>[]) { this._columns = cols; this.render(); }
  get columns(): TableColumn<T>[] { return this._columns; }

  set data(items: T[]) { this._data = items; this.render(); }
  get data(): T[] { return this._data; }

  set loading(val: boolean) { this._loading = val; this.render(); }
  get loading(): boolean { return this._loading; }

  set emptyMessage(msg: string) { this._emptyMessage = msg; }
  set emptyAction(action: string) { this._emptyAction = action; }

  connectedCallback() { this.render(); }

  private renderSkeletonRows(count = 5): string {
    return Array.from({ length: count }).map(() => `
      <tr>
        ${this._columns.map((col, i) => `
          <td style="${col.width ? `width: ${col.width};` : ""}">
            <div class="skeleton" style="height: 12px; width: ${i === 0 ? "70px" : i % 3 === 0 ? "90px" : "100%"}; border-radius: 2px;"></div>
          </td>
        `).join("")}
      </tr>
    `).join("");
  }

  private render() {
    // Caso em que o usuário forneceu uma tabela estática dentro do componente
    if (this._columns.length === 0) {
      const existingTable = this.querySelector("table");
      if (existingTable) {
        existingTable.classList.add("data-table");
        const container = existingTable.parentElement;
        if (container && !container.classList.contains("data-table-container")) {
          existingTable.parentElement?.classList.add("data-table-container");
        }
        return;
      }
    }

    const headerHtml = this._columns.map(col => `
      <th style="${col.width ? `width: ${col.width};` : ""}${col.align ? ` text-align: ${col.align};` : ""}">
        ${col.header}
      </th>
    `).join("");

    let bodyHtml = "";

    if (this._loading) {
      bodyHtml = this.renderSkeletonRows();
    } else if (this._data.length === 0) {
      bodyHtml = `
        <tr>
          <td colspan="${String(this._columns.length || 1)}" style="padding: 0;">
            <div class="empty-state" style="border: none; padding: 40px 24px;">
              <div class="empty-state-icon">
                ${EMPTY_ICON}
              </div>
              <div class="empty-state-title">${this._emptyMessage}</div>
              ${this._emptyAction ? `<div class="empty-state-action">${this._emptyAction}</div>` : ""}
            </div>
          </td>
        </tr>
      `;
    } else {
      bodyHtml = this._data.map(row => `
        <tr>
          ${this._columns.map(col => {
            const rawVal = col.render ? col.render(row) : (row[col.key] as string | number | undefined);
            const val = rawVal !== undefined ? String(rawVal) : "—";
            return `<td style="${col.align ? `text-align: ${col.align};` : ""}">${val}</td>`;
          }).join("")}
        </tr>
      `).join("");
    }

    this.innerHTML = `
      <div class="data-table-container">
        <table class="data-table">
          <thead>
            <tr>${headerHtml}</tr>
          </thead>
          <tbody>${bodyHtml}</tbody>
        </table>
      </div>
    `;
  }
}

if (!customElements.get("app-data-table")) {
  customElements.define("app-data-table", AppDataTable);
}

export class AppFilterBar extends HTMLElement {
  connectedCallback() {
    this.classList.add("filter-bar");
  }
}

if (!customElements.get("app-filter-bar")) {
  customElements.define("app-filter-bar", AppFilterBar);
}
